import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Tabs,
  Tab,
  Divider,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Alert,
} from '@mui/material';

const SettingsPage = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [saved, setSaved] = useState(false);

  const handleSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" fontWeight={600} sx={{ mb: 1 }}>
        Settings
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Configure your unified platform preferences
      </Typography>

      {saved && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Settings saved successfully
        </Alert>
      )}

      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="General" />
          <Tab label="Account" />
          <Tab label="Integrations" />
          <Tab label="Notifications" />
          <Tab label="Advanced" />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {activeTab === 0 && (
            <Box>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                General Settings
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FormControlLabel
                  control={<Switch defaultChecked />}
                  label="Collapse sidebar on startup"
                />
                <FormControlLabel
                  control={<Switch defaultChecked />}
                  label="Show tool icons in sidebar"
                />
                <FormControlLabel
                  control={<Switch defaultChecked />}
                  label="Enable keyboard shortcuts"
                />
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" fontWeight={600}>
                  Default Landing Page
                </Typography>
                <TextField
                  select
                  size="small"
                  defaultValue="dashboard"
                  SelectProps={{ native: true }}
                  sx={{ maxWidth: 300 }}
                >
                  <option value="dashboard">Dashboard</option>
                  <option value="projects">Projects</option>
                  <option value="oasis">Oasis</option>
                  <option value="campaigns">Campaigns</option>
                </TextField>
              </Box>
            </Box>
          )}

          {activeTab === 1 && (
            <Box>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                Account Settings
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 400 }}>
                <TextField label="Email" defaultValue="lecturertoni@gmail.com" size="small" disabled />
                <TextField label="Display Name" defaultValue="APH Team" size="small" />
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" fontWeight={600}>
                  Change Password
                </Typography>
                <TextField label="Current Password" type="password" size="small" />
                <TextField label="New Password" type="password" size="small" />
                <TextField label="Confirm New Password" type="password" size="small" />
              </Box>
            </Box>
          )}

          {activeTab === 2 && (
            <Box>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                Integrations
              </Typography>
              <Alert severity="info" sx={{ mb: 3 }}>
                Connect external services to enhance your workflow
              </Alert>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Google Drive
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Access and attach files from Google Drive
                      </Typography>
                    </Box>
                    <Button variant="outlined" size="small">
                      Connect
                    </Button>
                  </Box>
                </Paper>

                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Canva
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Create and import designs directly
                      </Typography>
                    </Box>
                    <Button variant="outlined" size="small">
                      Connect
                    </Button>
                  </Box>
                </Paper>

                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Ollama (Local AI)
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Connected via /api/ollama
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="success.main" fontWeight={600}>
                      ✓ Connected
                    </Typography>
                  </Box>
                </Paper>
              </Box>
            </Box>
          )}

          {activeTab === 3 && (
            <Box>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                Notification Preferences
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FormControlLabel
                  control={<Switch defaultChecked />}
                  label="Campaign updates"
                />
                <FormControlLabel
                  control={<Switch defaultChecked />}
                  label="New signals from Signal/Noise"
                />
                <FormControlLabel
                  control={<Switch defaultChecked />}
                  label="CRM contact changes"
                />
                <FormControlLabel
                  control={<Switch defaultChecked />}
                  label="AI Excellence Hub orders"
                />
                <FormControlLabel
                  control={<Switch />}
                  label="Desktop notifications"
                />
              </Box>
            </Box>
          )}

          {activeTab === 4 && (
            <Box>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                Advanced Settings
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FormControlLabel
                  control={<Switch />}
                  label="Developer mode"
                />
                <FormControlLabel
                  control={<Switch />}
                  label="Show API request logs in console"
                />
                <FormControlLabel
                  control={<Switch />}
                  label="Enable experimental features"
                />
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" fontWeight={600} color="error">
                  Danger Zone
                </Typography>
                <Button variant="outlined" color="error" size="small" sx={{ maxWidth: 200 }}>
                  Clear all local data
                </Button>
              </Box>
            </Box>
          )}

          <Divider sx={{ my: 3 }} />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="contained" onClick={handleSave}>
              Save Settings
            </Button>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default SettingsPage;
